'use client'

import React, { useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { RecaptchaProvider } from '@/components/shared/RecaptchaProvider'
import {
  Field,
  FormError,
  FormSuccess,
  SubmitButton,
  TextArea,
  TextInput,
} from '@/components/site/fields'

// Used on /kontakt and /ueber-uns; `sourcePage` tells the team where a lead came from.
export function ContactForm(props) {
  return (
    <RecaptchaProvider>
      <ContactFormInner {...props} />
    </RecaptchaProvider>
  )
}

function ContactFormInner({ sourcePage = '/kontakt' }) {
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [status, setStatus] = useState('idle') // idle, loading, success, error
  // A sub-second fill is a bot, not a person.
  const shownAt = React.useRef(Date.now())

  const handleSubmit = async e => {
    e.preventDefault()
    setStatus('loading')
    const form = e.currentTarget
    const data = Object.fromEntries(new FormData(form).entries())
    const name = [data.firstname, data.lastname]
      .filter(Boolean)
      .join(' ')
      .trim()

    try {
      const recaptchaToken = executeRecaptcha
        ? await executeRecaptcha('lead_contact')
        : undefined
      const res = await fetch('/api/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'contact',
          name,
          email: data.email,
          message: [
            data.subject ? `Betreff: ${data.subject}` : '',
            data.message,
          ]
            .filter(Boolean)
            .join('\n\n'),
          website: data.website, // honeypot
          formTime: Date.now() - shownAt.current,
          source_page: sourcePage,
          recaptcha_token: recaptchaToken,
        }),
      })
      if (!res.ok) throw new Error('error')
      setStatus('success')
      form.reset()
    } catch {
      setStatus('error')
    }
  }

  if (status === 'success') {
    return (
      <FormSuccess
        title="Vielen Dank!"
        action={
          <button
            type="button"
            onClick={() => setStatus('idle')}
            className="mt-2 text-[15px] font-semibold text-primary underline-offset-4 hover:underline"
          >
            Weitere Nachricht senden
          </button>
        }
      >
        Ihre Nachricht wurde gesendet. Wir melden uns in der Regel innerhalb von
        24 Stunden bei Ihnen.
      </FormSuccess>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-2xl font-[450] text-ink [font-stretch:110%]">
        Nachricht senden
      </h2>
      <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
        {/* Honeypot (hidden; bots fill it) */}
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          className="hidden"
          aria-hidden="true"
        />

        <div className="grid gap-5 sm:grid-cols-2">
          <Field id="firstname" label="Vorname" required>
            <TextInput
              id="firstname"
              name="firstname"
              required
              autoComplete="given-name"
              placeholder="Max"
            />
          </Field>
          <Field id="lastname" label="Nachname" required>
            <TextInput
              id="lastname"
              name="lastname"
              required
              autoComplete="family-name"
              placeholder="Muster"
            />
          </Field>
        </div>

        <Field id="email" label="E-Mail" required>
          <TextInput
            id="email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="max@muster.ch"
          />
        </Field>

        <Field id="subject" label="Betreff" required>
          <TextInput
            id="subject"
            name="subject"
            required
            placeholder="Anfrage zu …"
          />
        </Field>

        <Field id="message" label="Nachricht" required>
          <TextArea
            id="message"
            name="message"
            required
            placeholder="Wie können wir Ihnen helfen?"
            className="min-h-[160px]"
          />
        </Field>

        {status === 'error' && (
          <FormError>
            Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut oder
            schreiben Sie an info@immo-otti.ch.
          </FormError>
        )}

        <SubmitButton loading={status === 'loading'}>Absenden</SubmitButton>

        <p className="text-center text-xs text-graphite">
          Mit dem Absenden stimmen Sie unserer{' '}
          <a
            href="/datenschutz"
            className="underline underline-offset-2 hover:text-ink"
          >
            Datenschutzerklärung
          </a>{' '}
          zu.
        </p>
      </form>
    </div>
  )
}
